/* eslint-disable no-unused-vars */
import axios from 'axios';
import { toast } from 'sonner';
import { getToken } from '../../services/tokenService';
import { showFormErrorFeedback } from '../../lib/formFeedback';
import { computeTtcFromHtAndTaxes, normalizeTaxesSource } from '../../lib/computeTtc';
import { API } from './constants';

export function useCalculationLogic(deps) {
  const {
    activitesCompletes, autoCalcDoneRef, canAccessAnnuel, canAccessNominative, clearAllErrors, eligibilityForm,
    eligibilityResult, eligibilityValid, formData, getSeuilCAActif, isOnline, isSuperAdmin,
    modeCalcul, montantsPersonnalises, resultat, saveToLocal, selectedActivites2nd, selectedActivitesGO,
    selectedActivitesPIB, selectedActivitesTP, setActivitesCompletes, setEligibilityResult, setError, setFormData,
    setLoading, setPropositionConversion, setResultat, setTaxesPersonnalisees, taxesPersonnalisees,
  } = deps;

  const ajouterLigneTaxe = () => {
    const newId = Math.max(...taxesPersonnalisees.lignes.map(l => l.id), 0) + 1;
    setTaxesPersonnalisees(prev => ({
      ...prev,
      lignes: [...prev.lignes, { id: newId, nom: '', taux: '', montant: '', actif: true }]
    }));
  };

  const supprimerLigneTaxe = (id) => {
    setTaxesPersonnalisees(prev => ({
      ...prev,
      lignes: prev.lignes.filter(l => l.id !== id)
    }));
  };

  const updateLigneTaxe = (id, field, value) => {
    setTaxesPersonnalisees(prev => ({
      ...prev,
      lignes: prev.lignes.map(l => l.id === id ? { ...l, [field]: value } : l)
    }));
  };

  const getTaxesFinals = () => {
    if (!resultat) return null;
    
    const montantHT = getMontantsFinals()?.montant_ht || resultat.montant_ht || 0;
    
    if (taxesPersonnalisees.enabled) {
      const lignesActives = taxesPersonnalisees.lignes.filter(l => l.actif && l.nom);
      return {
        detail: lignesActives.map(l => ({
          nom: l.nom,
          taux: l.taux ? parseFloat(l.taux) : null,
          montant: l.montant ? parseFloat(l.montant) : (l.taux ? montantHT * parseFloat(l.taux) / 100 : 0)
        })),
        is_personnalise: true
      };
    }
    
    return resultat.taxes || null;
  };

  const getMontantsFinals = () => {
    if (!resultat) return null;
    
    if (montantsPersonnalises.enabled) {
      const caFinal = montantsPersonnalises.ca_personnalise 
        ? parseFloat(montantsPersonnalises.ca_personnalise) 
        : parseFloat(formData.ca_ht) || 0;
      
      let htFinal = montantsPersonnalises.montant_ht_personnalise 
        ? parseFloat(montantsPersonnalises.montant_ht_personnalise) * (resultat.prorata?.facteur || 1)
        : resultat.montant_ht || 0;
      
      // Appliquer la remise % puis la remise € (cumulables)
      if (montantsPersonnalises.remise_pourcentage) {
        const remise = parseFloat(montantsPersonnalises.remise_pourcentage);
        if (remise > 0 && remise <= 100) {
          htFinal = htFinal * (1 - remise / 100);
        }
      }
      if (montantsPersonnalises.remise_euros) {
        const remiseEur = parseFloat(montantsPersonnalises.remise_euros);
        if (remiseEur > 0) {
          htFinal = Math.max(0, htFinal - remiseEur);
        }
      }

      // V37.34 — Calcul du TTC delegue a l'utilitaire partagee
      // `computeTtcFromHtAndTaxes` (frontend/src/lib/computeTtc.js), elle-meme
      // alignee sur le backend `services/ttc_calculator.py`. Cela garantit
      // la coherence Simulateur Annuel / Nominative / Avenant / PDF Devis ESS.
      let ttcFinal;
      if (montantsPersonnalises.montant_ttc_personnalise) {
        ttcFinal = parseFloat(montantsPersonnalises.montant_ttc_personnalise);
      } else {
        const taxesSource = normalizeTaxesSource(
          taxesPersonnalisees,
          resultat?.taxes,
        );
        const { ttc } = computeTtcFromHtAndTaxes(htFinal, taxesSource, 0.085);
        ttcFinal = ttc;
      }
      
      return {
        ca: caFinal,
        montant_ht: htFinal,
        montant_ttc: ttcFinal,
        remise: montantsPersonnalises.remise_pourcentage ? parseFloat(montantsPersonnalises.remise_pourcentage) : 0,
        remise_euros: montantsPersonnalises.remise_euros ? parseFloat(montantsPersonnalises.remise_euros) : 0,
        is_personnalise: true
      };
    }
    
    return {
      ca: parseFloat(formData.ca_ht) || 0,
      montant_ht: resultat.montant_ht || 0,
      montant_ttc: resultat.montant_ttc || 0,
      remise: 0,
      remise_euros: 0,
      is_personnalise: false
    };
  };

  const handleActivitesLoaded = (categorie, activites) => {
    setActivitesCompletes(prev => ({
      ...prev,
      [categorie]: activites
    }));
  };

  const getActivitesSelectionneesFull = () => {
    return {
      gros_oeuvre: activitesCompletes.gros_oeuvre.filter(a => selectedActivitesGO.includes(a.id)),
      second_oeuvre: activitesCompletes.second_oeuvre.filter(a => selectedActivites2nd.includes(a.id)),
      travaux_publics: activitesCompletes.travaux_publics.filter(a => selectedActivitesTP.includes(a.id)),
      pib: activitesCompletes.pib.filter(a => selectedActivitesPIB.includes(a.id))
    };
  };

  const handleCalculer = async () => {
    // Réinitialiser les erreurs
    clearAllErrors();
    let hasValidationErrors = false;
    // V37.42 — Conserve le premier champ en erreur pour pouvoir y scroller
    let firstErrorField = null;

    // Vérifier les permissions
    if (modeCalcul === 'annuel' && !canAccessAnnuel) {
      toast.error('Accès réservé aux COOPER\'S et Super Administrateurs');
      return;
    }
    if (modeCalcul === 'nominative' && !canAccessNominative) {
      toast.error('Accès réservé aux Membres et Super Administrateurs');
      return;
    }

    // Validation du CA HT avec erreur inline
    if (!formData.ca_ht || parseFloat(formData.ca_ht) <= 0) {
      setError('ca_ht', 'Veuillez saisir un chiffre d\'affaires valide (supérieur à 0)');
      hasValidationErrors = true;
      if (!firstErrorField) firstErrorField = 'ca-ht-input';
    }

    // === VÉRIFICATION DU SEUIL CA PAR TYPE (annuel ou nominatif) ===
    const seuilActif = getSeuilCAActif();
    if (seuilActif.actif && formData.ca_ht) {
      const caValue = parseFloat(formData.ca_ht);
      if (caValue < seuilActif.seuil) {
        const modeLabel = modeCalcul === 'nominative' ? 'RCDS Nominatif' : 'RCDS Annuel';
        const messageErreur = seuilActif.message || 
          `Le chiffre d'affaires minimum requis pour ${modeLabel} est de ${seuilActif.seuil.toLocaleString('fr-FR')} €. Votre CA (${caValue.toLocaleString('fr-FR')} €) est inférieur au seuil configuré.`;
        setError('ca_ht', messageErreur);
        hasValidationErrors = true;
        if (!firstErrorField) firstErrorField = 'ca-ht-input';
      }
    }

    // Validation du nombre de jours pour le mode nominative
    if (modeCalcul === 'nominative') {
      if (!formData.nombre_jours || parseInt(formData.nombre_jours) <= 0) {
        setError('nombre_jours', 'Veuillez saisir un nombre de jours valide');
        hasValidationErrors = true;
        if (!firstErrorField) firstErrorField = 'nombre-jours-input';
      } else if (parseInt(formData.nombre_jours) > 360) {
        setError('nombre_jours', 'Le nombre de jours ne peut pas dépasser 360');
        hasValidationErrors = true;
        if (!firstErrorField) firstErrorField = 'nombre-jours-input';
      }
    }

    // Validation des activités
    const totalActivites = selectedActivitesGO.length + selectedActivites2nd.length + 
                           selectedActivitesPIB.length + selectedActivitesTP.length;
    
    if (totalActivites === 0) {
      setError('activites', 'Veuillez sélectionner au moins une activité');
      hasValidationErrors = true;
      if (!firstErrorField) firstErrorField = 'activites-section';
    }

    // Arrêter si erreurs de validation
    if (hasValidationErrors) {
      // V37.42 / V37.43 — Centralisation via helper `showFormErrorFeedback` :
      // toast top-level + scroll automatique vers le premier champ en erreur
      // + halo rouge clignotant + focus auto. Evite l'effet "bouton muet"
      // quand un COOPER ouvre le simulateur via prefill membre sans CA.
      showFormErrorFeedback({
        firstErrorTestId: firstErrorField,
        message: "Veuillez corriger les erreurs du formulaire avant de calculer",
      });
      return;
    }

    setLoading(true);
    setResultat(null);

    // === MODE HORS LIGNE: Sauvegarde locale ===
    if (!isOnline) {
      try {
        const offlineSimulation = {
          type: 'batico',
          mode: modeCalcul,
          formData: {
            ca_ht: formData.ca_ht,
            nombre_jours: formData.nombre_jours
          },
          activites: {
            gros_oeuvre: selectedActivitesGO,
            second_oeuvre: selectedActivites2nd,
            pib: selectedActivitesPIB,
            travaux_publics: selectedActivitesTP
          },
          // Calcul local approximatif (sera recalculé en ligne)
          resultat: {
            montant_ht: parseFloat(formData.ca_ht) * 0.003, // Estimation
            montant_ttc: parseFloat(formData.ca_ht) * 0.003 * 1.085,
            offline_estimation: true,
            message: 'Estimation hors ligne - Recalculer en ligne pour le montant exact'
          }
        };

        const saved = await saveToLocal(offlineSimulation);
        if (saved) {
          setResultat(offlineSimulation.resultat);
          toast.warning('Mode hors ligne: Simulation sauvegardée localement', {
            description: 'Le calcul exact sera effectué à la reconnexion',
            duration: 5000
          });
        }
      } catch (error) {
        toast.error('Erreur lors de la sauvegarde hors ligne');
      } finally {
        setLoading(false);
      }
      return;
    }

    try {
      const token = getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      // === ÉTAPE 1 : SCORING D'ÉLIGIBILITÉ (avant tout calcul tarifaire) ===
      // Conformément au pseudo-code scorer_dossier_rcds :
      //   - si classe = VALIDATION_MANUELLE → on STOPPE le calcul automatique
      //   - sinon → on récupère le coefficient_dossier et on lance le calcul tarif
      const nbActivites =
        (selectedActivitesGO?.length || 0) +
        (selectedActivites2nd?.length || 0) +
        (selectedActivitesPIB?.length || 0) +
        (selectedActivitesTP?.length || 0);

      let scoringResult = null;
      try {
        // On envoie cotisation_base=1.0 pour récupérer le scoring seul.
        // Le moteur retourne classe_dossier, coefficient_dossier, motifs (raisons_blocage + alertes).
        const sc = await axios.post(
          `${API}/batico/eligibility/compute`,
          {
            form_data: { ...eligibilityForm, nombre_activites_batico: nbActivites },
            cotisation_base: 1.0,
            famille_rcds: 'BATICO',
            nombre_activites: nbActivites,
          },
          { headers }
        );
        if (sc.data?.ok) {
          scoringResult = sc.data;
          setEligibilityResult(sc.data);
        } else if (sc.data?.validation_errors) {
          toast.error(`Validation profil éligibilité : ${sc.data.validation_errors.join(' · ')}`);
        }
      } catch (e) {
        console.warn('Scoring éligibilité échoué — calcul tarifaire continuera sans coef:', e?.response?.data || e.message);
      }

      // === V36.62 — REFUS : Super Admin peut forcer la validation ===
      // Au lieu de bloquer durement l'UI, on continue le calcul tarifaire
      // mais on garde un eligibilityResult marqué REFUS. Le Super Admin
      // verra le bloc « Forcer la validation » dans la zone résultat et
      // pourra : (a) appliquer un taux personnalisé qui override REFUS,
      // ou (b) « Souscrire en l'état ».
      // Pour les non Super Admin, on bloque toujours.
      if (scoringResult && scoringResult.statut === 'REFUS' && !isSuperAdmin) {
        const motifs = [
          ...(scoringResult.raisons_blocage || []),
          ...(scoringResult.alertes || []),
        ];
        toast.error(
          `Calcul automatique impossible : dossier en REFUS.`,
          { duration: 6000 }
        );
        setResultat({
          requires_manual_validation: false,
          statut: scoringResult.statut,
          classe_dossier: scoringResult.classe_dossier,
          score_global: scoringResult.score_global,
          motifs: motifs,
          message: `Statut ${scoringResult.statut}. ${motifs.length ? 'Voir motifs ci-dessous.' : ''}`,
        });
        setLoading(false);
        return;
      }

      // === ÉTAPE 2 : CALCUL TARIFAIRE (coefficient_dossier appliqué côté backend si transmis) ===
      const requestData = {
        ca_ht: parseFloat(formData.ca_ht),
        activites_ids_go: selectedActivitesGO,
        activites_ids_2nd: selectedActivites2nd,
        activites_ids_pib: selectedActivitesPIB,
        activites_ids_tp: selectedActivitesTP,
        // Coefficient issu du scoring d'éligibilité (FAIBLE/STANDARD/SENSIBLE/RENFORCE)
        coefficient_dossier: scoringResult?.coefficient_dossier ?? null,
      };

      if (modeCalcul === 'nominative' && formData.nombre_jours) {
        requestData.nombre_jours = parseInt(formData.nombre_jours);
      }

      // Utiliser l'endpoint approprié selon le mode
      const endpoint = modeCalcul === 'annuel' 
        ? `${API}/tarificateur/batico/annuel`
        : `${API}/tarificateur/batico/nominative`;

      const response = await axios.post(endpoint, requestData, { headers });

      // Si on a un coef issu du scoring et que le backend ne l'a pas appliqué
      // (compatibilité ascendante), on l'applique côté front pour information.
      let resultData = response.data;
      if (scoringResult?.coefficient_dossier && resultData && !resultData.coefficient_dossier_applied) {
        const coef = Number(scoringResult.coefficient_dossier);
        const baseHt = Number(resultData.montant_ht || resultData.cotisation || 0);
        if (coef && coef !== 1 && baseHt > 0) {
          resultData = {
            ...resultData,
            montant_ht_avant_coef_dossier: baseHt,
            coefficient_dossier_applied: coef,
            classe_dossier: scoringResult.classe_dossier,
            montant_ht: +(baseHt * coef).toFixed(2),
          };
        } else {
          resultData = { ...resultData, coefficient_dossier_applied: coef, classe_dossier: scoringResult.classe_dossier };
        }
      }
      setResultat({ ...resultData, ca_calcule: parseFloat(formData.ca_ht) || 0 });

      // === Calcul éligibilité + cotisation ajustée final (modulateurs avancés) ===
      // (Déjà appelé en étape 1 — on enrichit juste avec la vraie cotisation_base)
      if (!eligibilityValid) {
        toast.warning("Profil d'éligibilité : les ventilations doivent faire 100%. Calcul de base affiché mais pas d'éligibilité finale.");
      }
      try {
        const cotisationBase = Number(resultData?.montant_ht || resultData?.cotisation || 0);
        if (cotisationBase > 0) {
          const eligRes = await axios.post(
            `${API}/batico/eligibility/compute`,
            {
              form_data: { ...eligibilityForm, nombre_activites_batico: nbActivites },
              cotisation_base: cotisationBase,
              famille_rcds: 'BATICO',
              nombre_activites: nbActivites,
            },
            { headers }
          );
          if (eligRes.data?.ok) {
            setEligibilityResult(eligRes.data);
          }
        }
      } catch (e) {
        console.warn('Eligibility compute (final) failed:', e?.response?.data || e.message);
      }
      
      // Sauvegarder également en local pour historique
      await saveToLocal({
        type: 'batico',
        mode: modeCalcul,
        formData: { ca_ht: formData.ca_ht, nombre_jours: formData.nombre_jours },
        activites: {
          gros_oeuvre: selectedActivitesGO,
          second_oeuvre: selectedActivites2nd,
          pib: selectedActivitesPIB,
          travaux_publics: selectedActivitesTP
        },
        resultat: response.data
      });
      
      toast.success('Calcul effectué avec succès');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du calcul');
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      ca_ht: '',
      nb_activites_go: 0,
      nb_activites_2nd: 0,
      nb_activites_pib: 0,
      nb_activites_tp: 0,
      nombre_jours: ''
    });
    setResultat(null);
    // Effacer la bannière de conversion : la simulation a changé
    setPropositionConversion(null);
    // Réautoriser un nouveau calcul automatique après reset
    autoCalcDoneRef.current = false;
  };

  return {
    ajouterLigneTaxe, getActivitesSelectionneesFull, getMontantsFinals, getTaxesFinals, handleActivitesLoaded,
    handleCalculer, handleReset, supprimerLigneTaxe, updateLigneTaxe,
  };
}

export default useCalculationLogic;
